import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map } from 'rxjs';
import { AccessService } from '../services/access/access.service';
import {
  AccessNavigationService,
  NO_ACCESS_URL,
} from '../services/access/access-navigation.service';
import { collectRequirements } from '../services/access/access-rules';
import { SessionService } from '../services/smartclock/session.service';

type GuardResult = boolean | UrlTree | Observable<boolean | UrlTree>;

/**
 * Espera a que los permisos estén cargados (dispara la carga si nadie la
 * pidió todavía) y luego decide.
 */
export function afterAccessLoaded(decide: () => boolean | UrlTree): GuardResult {
  const access = inject(AccessService);
  if (access.status() === 'idle') access.load();
  if (access.status() === 'loading') return access.whenSettled().pipe(map(decide));
  return decide();
}

/**
 * Guard único de acceso por permisos (defensa en profundidad; el API es la
 * autoridad). Sirve como `canActivate` y como `canActivateChild`.
 *
 * Lee de `data` (contrato §6): `permission` (string o string[]: TODAS),
 * `anyPermission` (AL MENOS UNA), `staff` (clave de staff), `inspector: true`
 * y `scope` (alcance de datos de la variante). Los requisitos del padre y del
 * hijo se SUMAN (recorre `pathFromRoot`). Una ruta sin requisitos sólo exige
 * estar autenticado (authGuard). Si deniega → `/app/sin-acceso`, nunca otro
 * módulo.
 */
export const accessGuard: CanActivateFn & CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const requirements = collectRequirements(route.pathFromRoot.map((r) => r.routeConfig?.data));
  if (!requirements.length) return true;

  const router = inject(Router);
  if (!inject(SessionService).current) return router.createUrlTree(['/login']);

  const navigation = inject(AccessNavigationService);
  return afterAccessLoaded(() =>
    navigation.canOpenSnapshot(route)
      ? true
      : router.createUrlTree([NO_ACCESS_URL], { queryParams: { from: state.url } }),
  );
};

/**
 * Inicio de /app: redirige al landing por defecto (ROLE_LANDING si lo puede
 * abrir, si no la primera entrada del menú). Si no hay ninguno, deja pasar a
 * la pantalla de inicio, que explica si no tiene módulos habilitados o si no
 * se pudieron cargar sus permisos.
 */
export const landingGuard: CanActivateFn = () => {
  const router = inject(Router);
  if (!inject(SessionService).current) return router.createUrlTree(['/login']);

  const navigation = inject(AccessNavigationService);
  return afterAccessLoaded(() => {
    const landing = navigation.landingUrl();
    return landing ? router.parseUrl(landing) : true;
  });
};
